/**
 * PR-36A — WhatsApp Viral Engine Core.
 *
 * Single source of truth for every share that originates from VerseSport
 * and lands in WhatsApp during the World Cup. Layered on top of existing
 * primitives (referrals, canonicalShare, nationWarsShare, autoReferral)
 * — never duplicates referral codes, never bypasses anti-fraud.
 *
 * Surfaces covered:
 *   - Prediction (A)
 *   - Victory (B)
 *   - Friend Challenge (C)
 *   - Nation Wars (D)
 *
 * Mobile first (F):
 *   - Android / iOS WhatsApp → `whatsapp://send?text=…` when possible,
 *     fallback to `https://wa.me/?text=…`.
 *   - Desktop / WhatsApp Web → `https://wa.me/?text=…` only.
 *   - Web Share API used when the device exposes it natively
 *     (Android Chrome / iOS Safari) so the user can pick the WhatsApp
 *     contact directly from the OS share sheet.
 *
 * Analytics (G):
 *   - `wa_share_initiated`   — user tapped a WA share affordance
 *   - `wa_share_completed`   — link or intent opened successfully
 *   - `wa_share_destination` — captures `{ kind, platform, channel }`
 *   - `wa_share_failed`      — open / clipboard failed
 *
 * Referral link integration (E):
 *   Every URL is forced through `buildCanonicalShareUrl(...)` which
 *   re-uses the user's `ss2026_ref_code` (or mints one) — guaranteeing
 *   no generic links ever leave the app.
 */
import { trackEvent } from "@/lib/audit";
import type { EventType } from "@/types";
import { buildCanonicalShareUrl } from "@/lib/share/canonicalShare";
import {
  buildNationShareLandingUrl,
  buildNationWarsMessage,
  getNationFlag,
  getNationName,
} from "@/lib/share/nationWarsShare";
import { getStoredLang, type Lang } from "@/lib/i18n";
import {
  buildPersonalClip,
  type PersonalClipPayload,
  type PersonalClipKind,
} from "@/lib/clipExport/personalClips";

// ---------------------------------------------------------------------------
// Premium-card auto attachment
// ---------------------------------------------------------------------------
// Every high-level share entry point (prediction / victory / challenge /
// nation) MUST reach the OS share sheet with the same premium PNG that the
// Viral Cards gallery exports. This helper turns per-kind input into a
// PersonalClipPayload, renders it via the shared canvas engine, and returns
// a File suitable for `navigator.share({ files })`. If rendering fails
// (SSR, missing DOM, older browser) we silently return null so the caller
// still falls back to text/link share.

function payloadForPrediction(p: PredictionShareInput): PersonalClipPayload {
  return {
    kind: "mi_prediccion",
    nation: p.nationCode ?? undefined,
    headline: p.pickLabel,
    subline: p.matchLabel,
    handle: "VerseSport",
  };
}

function payloadForVictory(v: VictoryShareInput): PersonalClipPayload {
  const isBadgeOnly = !!v.badgeLabel && (!v.matchLabel || v.xpEarned == null);
  const kind: PersonalClipKind = isBadgeOnly ? "nuevo_badge" : "mi_clip_viral";
  const minutePart = v.minute != null ? ` · ${v.minute}'` : "";
  const scorePart = v.score ? ` · ${v.score.home}-${v.score.away}` : "";
  const headline =
    v.momentLabel ??
    (v.xpEarned != null ? `+${v.xpEarned} XP` : v.badgeLabel ?? "¡ACIERTO!");
  const subline = `${v.matchLabel ?? ""}${minutePart}${scorePart}`.trim() || undefined;
  return {
    kind,
    nation: v.nationCode ?? undefined,
    headline,
    subline,
    reward: v.badgeLabel ?? (v.xpEarned != null ? `${v.xpEarned} XP` : undefined),
    handle: "VerseSport",
  };
}

function payloadForChallenge(c: ChallengeShareInput): PersonalClipPayload {
  return {
    kind: "batalla_naciones",
    nation: c.nationCode ?? undefined,
    headline: c.friendName ? `${c.friendName}, te reto` : "Te reto",
    subline: c.matchLabel ?? "Predicción vs predicción",
    handle: "VerseSport",
  };
}

function payloadForNation(n: NationShareInput): PersonalClipPayload {
  return {
    kind: "mi_nacion",
    nation: n.nationCode,
    position: n.position ?? undefined,
    totalCompetitors: n.totalNations ?? undefined,
    handle: "VerseSport",
  };
}

async function buildAttachment(
  payload: PersonalClipPayload,
  filenameHint: string,
): Promise<File[] | undefined> {
  if (typeof document === "undefined" || typeof fetch === "undefined") return undefined;
  try {
    const dataUrl = await buildPersonalClip(payload);
    const blob = await (await fetch(dataUrl)).blob();
    return [new File([blob], filenameHint, { type: "image/png" })];
  } catch {
    return undefined;
  }
}

async function withPremiumAttachment<T extends { attachments?: File[] }>(
  opts: T,
  build: () => Promise<File[] | undefined>,
): Promise<T> {
  if (opts.attachments && opts.attachments.length > 0) return opts;
  const files = await build();
  if (!files || files.length === 0) return opts;
  return { ...opts, attachments: files };
}

export type WaShareKind = "prediction" | "victory" | "challenge" | "nation";
export type WaPlatform = "android" | "ios" | "desktop" | "unknown";
export type WaChannel = "native_share" | "whatsapp_app" | "wa_me" | "copy";

export interface PredictionShareInput {
  matchLabel: string;          // e.g. "ESP vs ARG"
  pickLabel: string;           // "México 2-1 Argentina" or "Empate"
  nationCode?: string | null;  // user's selected nation for flavour
  predictionId?: string | null;
  kickoffISO?: string | null;
}

export interface VictoryShareInput {
  /** Match label (e.g. "ESP vs ARG"). Optional for moment/badge-only victories. */
  matchLabel?: string | null;
  /** XP earned. Optional — omit for moment/badge surfaces with no XP context. */
  xpEarned?: number | null;
  badgeLabel?: string | null;
  nationCode?: string | null;
  predictionId?: string | null;
  /**
   * Optional viral-moment context (goal / capsule / recap / live moments).
   * When present, message template adapts to a moment-style copy instead of
   * the default "I nailed X and earned Y XP" line.
   */
  momentLabel?: string | null;
  momentKind?: string | null;
  /** Goal minute, used when momentKind === "goal" or for live moments. */
  minute?: number | null;
  /** Live score snapshot (home/away). */
  score?: { home: number; away: number } | null;
}

export interface ChallengeShareInput {
  code: string;                // friend-challenge code
  matchLabel?: string | null;
  friendName?: string | null;
  nationCode?: string | null;
}

export interface NationShareInput {
  nationCode: string;
  position?: number | null;
  totalNations?: number | null;
  trend?: "up" | "down" | "flat" | null;
}

export interface ShareResult {
  ok: boolean;
  channel: WaChannel;
  url: string;
  message: string;
}

// ---------------------------------------------------------------------------
// Platform detection (mobile-first)
// ---------------------------------------------------------------------------

export function detectPlatform(): WaPlatform {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  return "desktop";
}

function isMobile(p: WaPlatform): boolean {
  return p === "android" || p === "ios";
}

// ---------------------------------------------------------------------------
// URL / link helpers
// ---------------------------------------------------------------------------

function utmFor(kind: WaShareKind, campaignOverride?: string) {
  return {
    source: "whatsapp",
    medium: "share",
    campaign: campaignOverride && campaignOverride.length > 0 ? campaignOverride : `wa_${kind}`,
  };
}

/** Build canonical, referral-tagged URL for a kind+path. */
function buildUrl(kind: WaShareKind, path: string, campaignOverride?: string): string {
  return buildCanonicalShareUrl(path, utmFor(kind, campaignOverride));
}

function waMeLink(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

function whatsappAppLink(text: string): string {
  return `whatsapp://send?text=${encodeURIComponent(text)}`;
}

// ---------------------------------------------------------------------------
// Message builders (viral-optimised, ES-LATAM tone, Mundial flavour)
// ---------------------------------------------------------------------------

function flagPrefix(nation?: string | null): string {
  const flag = getNationFlag(nation ?? null);
  return flag === "🌎" ? "" : `${flag} `;
}

function resolveLang(lang?: Lang): Lang {
  return lang ?? getStoredLang();
}

const PREDICTION_TPL: Record<Lang, (match: string, pick: string) => string> = {
  ES: (m, p) => `Mi predicción para ${m}: ${p}.\n¿Te animas a superarme en VerseSport? 🏆`,
  EN: (m, p) => `My prediction for ${m}: ${p}.\nThink you can beat me on VerseSport? 🏆`,
  PT: (m, p) => `Meu palpite para ${m}: ${p}.\nAchas que me supera no VerseSport? 🏆`,
};

const VICTORY_TPL: Record<Lang, (match: string, xp: number, badge: string) => string> = {
  ES: (m, xp, b) => `¡Acerté ${m} y gané +${xp} XP${b}!\nPredice tú también en VerseSport y compite conmigo 🔥`,
  EN: (m, xp, b) => `Nailed ${m} and earned +${xp} XP${b}!\nMake your prediction on VerseSport and compete with me 🔥`,
  PT: (m, xp, b) => `Acertei ${m} e ganhei +${xp} XP${b}!\nPalpite você também no VerseSport e compita comigo 🔥`,
};
const VICTORY_BADGE_TPL: Record<Lang, (badge: string) => string> = {
  ES: (b) => ` y desbloqueé «${b}»`,
  EN: (b) => ` and unlocked "${b}"`,
  PT: (b) => ` e desbloqueei «${b}»`,
};

/** Badge-only victory (no match / no XP). */
const BADGE_ONLY_TPL: Record<Lang, (badge: string) => string> = {
  ES: (b) => `🏅 Acabo de desbloquear «${b}» en VerseSport.\n¿Puedes superarme? 🔥`,
  EN: (b) => `🏅 Just unlocked "${b}" on VerseSport.\nThink you can beat me? 🔥`,
  PT: (b) => `🏅 Acabei de desbloquear «${b}» no VerseSport.\nAchas que me supera? 🔥`,
};

/** Generic moment template — live goal / big moment / cinematic recap. */
const MOMENT_TPL: Record<Lang, (label: string, scoreLine: string) => string> = {
  ES: (l, s) => `${l}${s}\nVívelo conmigo en VerseSport 🔥`,
  EN: (l, s) => `${l}${s}\nLive it with me on VerseSport 🔥`,
  PT: (l, s) => `${l}${s}\nVive comigo no VerseSport 🔥`,
};

const CHALLENGE_TPL: Record<Lang, (who: string, match: string) => string> = {
  ES: (w, m) => `${w}te reto${m} en VerseSport.\nPredicción vs predicción. Gana el que más sepa de fútbol 🏆`,
  EN: (w, m) => `${w}I challenge you${m} on VerseSport.\nPrediction vs prediction. Best football brain wins 🏆`,
  PT: (w, m) => `${w}te desafio${m} no VerseSport.\nPalpite contra palpite. Vence quem mais entende de futebol 🏆`,
};
const CHALLENGE_IN_TPL: Record<Lang, (match: string) => string> = {
  ES: (m) => ` en ${m}`,
  EN: (m) => ` on ${m}`,
  PT: (m) => ` em ${m}`,
};

const NATION_LINE_TPL: Record<Lang, (flag: string, name: string, pos: number, total: string, arrow: string) => string> = {
  ES: (f, n, p, t, a) => `${f} ${n} va #${p}${t} en Nation Wars ${a}`,
  EN: (f, n, p, t, a) => `${f} ${n} is #${p}${t} in Nation Wars ${a}`,
  PT: (f, n, p, t, a) => `${f} ${n} está #${p}${t} em Nation Wars ${a}`,
};

export function buildPredictionMessage(p: PredictionShareInput, lang?: Lang): string {
  const L = resolveLang(lang);
  const flag = flagPrefix(p.nationCode);
  return `${flag}${PREDICTION_TPL[L](p.matchLabel, p.pickLabel)}`;
}

export function buildVictoryMessage(v: VictoryShareInput, lang?: Lang): string {
  const L = resolveLang(lang);
  const flag = flagPrefix(v.nationCode);
  // 1) Moment context wins — used by goal / capsule / recap surfaces.
  if (v.momentLabel || v.momentKind) {
    const minutePart = v.minute != null ? ` ${v.minute}'` : "";
    const scorePart =
      v.score
        ? ` · ${v.score.home}-${v.score.away}`
        : v.matchLabel
          ? ` · ${v.matchLabel}`
          : "";
    const head = v.momentLabel ?? v.momentKind ?? "";
    return `${flag}${MOMENT_TPL[L](`${head}${minutePart}`, scorePart)}`;
  }
  // 2) Badge-only victory (no match / no XP).
  if (v.badgeLabel && (!v.matchLabel || v.xpEarned == null)) {
    return `${flag}${BADGE_ONLY_TPL[L](v.badgeLabel)}`;
  }
  // 3) Standard match victory.
  const badge = v.badgeLabel ? VICTORY_BADGE_TPL[L](v.badgeLabel) : "";
  return `${flag}${VICTORY_TPL[L](v.matchLabel ?? "", v.xpEarned ?? 0, badge)}`;
}

export function buildChallengeMessage(c: ChallengeShareInput, lang?: Lang): string {
  const L = resolveLang(lang);
  const flag = flagPrefix(c.nationCode);
  const match = c.matchLabel ? CHALLENGE_IN_TPL[L](c.matchLabel) : "";
  const who = c.friendName ? `${c.friendName}, ` : "";
  return `${flag}${CHALLENGE_TPL[L](who, match)}`;
}

export function buildNationMessage(n: NationShareInput, lang?: Lang): string {
  const L = resolveLang(lang);
  const base = buildNationWarsMessage(n.nationCode, L);
  if (!n.position) return base;
  const flag = getNationFlag(n.nationCode);
  const name = getNationName(n.nationCode, L);
  const arrow = n.trend === "up" ? "📈" : n.trend === "down" ? "📉" : "🏁";
  const total = n.totalNations ? `/${n.totalNations}` : "";
  return `${NATION_LINE_TPL[L](flag, name, n.position, total, arrow)}\n${base}`;
}

// ---------------------------------------------------------------------------
// URL builders per kind
// ---------------------------------------------------------------------------

export function buildPredictionUrl(p: PredictionShareInput): string {
  const path = p.predictionId ? `/?pred=${encodeURIComponent(p.predictionId)}` : "/";
  return buildUrl("prediction", path);
}

export function buildVictoryUrl(v: VictoryShareInput): string {
  const path = v.predictionId ? `/?win=${encodeURIComponent(v.predictionId)}` : "/";
  return buildUrl("victory", path);
}

export function buildChallengeUrl(c: ChallengeShareInput): string {
  return buildUrl("challenge", `/challenge/${encodeURIComponent(c.code)}`);
}

export function buildNationUrl(n: NationShareInput): string {
  return buildUrl("nation", buildNationShareLandingUrl(n.nationCode).replace(/^https?:\/\/[^/]+/, ""));
}

// ---------------------------------------------------------------------------
// Core share entry points
// ---------------------------------------------------------------------------

interface ShareOptions {
  /** Allow native OS share sheet (recommended on mobile). Default: true. */
  preferNative?: boolean;
  /** Surface label for analytics (e.g. "prediction_card", "victory_toast"). */
  source?: string;
  /** Optional explicit language override. Falls back to stored lang. */
  lang?: Lang;
  /**
   * Optional file attachments (e.g. generated PNG goal card). Forwarded to
   * `navigator.share({ files })` when `canShare({ files })` is true.
   * Falls through to the standard WA-app / wa.me chain if the OS rejects
   * file sharing — the link share keeps working.
   */
  attachments?: File[];
  /**
   * Optional path/URL override. When set, replaces the per-kind default path
   * (e.g. `/api/public/og-match?…` so a dynamic OG image preview is preserved).
   * Still routed through `buildCanonicalShareUrl` so referrals + UTM + canonical
   * host normalisation stay intact.
   */
  customPath?: string;
  /**
   * Optional UTM campaign override. When set, replaces the default per-kind
   * `wa_<kind>` campaign in the generated share URL. Used by viral-moment
   * surfaces (capsule / recap / goal boost) that want to preserve a per-moment
   * campaign tag (e.g. `goal`, `comeback`) while still routing through the
   * engine. Falls back to the default campaign when undefined or empty.
   */
  campaignOverride?: string;
  /**
   * Generic legacy bridge — emit parallel legacy events on share success so
   * activation / scoring / creator-studio counters keep working while
   * surfaces migrate to the engine. Pass a single `EventType` to bridge all
   * channels, or per-channel mapping. `payload` is merged into the legacy
   * event payload alongside `{ share_source, share_platform }`.
   * Failures / aborts do NOT emit bridge events.
   * The Nation Wars bridge (Phase 3A) remains independent.
   */
  legacyEvents?:
    | EventType
    | {
        native?: EventType;
        whatsapp?: EventType;
        copy?: EventType;
        payload?: Record<string, unknown>;
      };
}

async function track(name: EventType, payload: Record<string, unknown>) {
  try { await trackEvent(name, payload); } catch { /* non-fatal */ }
}

/**
 * Map engine channel → legacy `nation_wars_share_*` platform value.
 * native_share → "native"
 * whatsapp_app | wa_me → "whatsapp"
 * copy → "copy"
 */
function legacyNationPlatform(channel: WaChannel): "native" | "whatsapp" | "copy" {
  if (channel === "native_share") return "native";
  if (channel === "copy") return "copy";
  return "whatsapp";
}

/**
 * Temporary legacy bridge — emits `nation_wars_share_*` events in parallel
 * with the central `wa_share_*` events so existing consumers
 * (activation scoring, creator studio counters) keep working while the
 * Nation Wars surfaces migrate to the engine. Only fires for nation shares.
 */
function emitNationLegacyBridge(
  channel: WaChannel,
  source: string,
  nationCode: string | null | undefined,
) {
  const platform = legacyNationPlatform(channel);
  const payload = {
    share_source: source,
    share_target: "nation-wars",
    share_platform: platform,
    nation_code: nationCode ?? null,
  };
  const event: EventType =
    platform === "native"
      ? "nation_wars_share_native"
      : platform === "copy"
        ? "nation_wars_share_copy"
        : "nation_wars_share_whatsapp";
  void track(event, payload);
}

/**
 * Generic, opt-in legacy bridge driven by call-site `legacyEvents`.
 * Emits one legacy event per successful share, mirroring `wa_share_completed`
 * so existing analytics consumers keep working without coupling the engine
 * to per-surface event taxonomies. No-op when `legacyEvents` is undefined.
 */
function emitCallerLegacyBridge(
  channel: WaChannel,
  source: string,
  legacyEvents: ShareOptions["legacyEvents"],
) {
  if (!legacyEvents) return;
  const platform = legacyNationPlatform(channel);
  let event: EventType | undefined;
  let extra: Record<string, unknown> = {};
  if (typeof legacyEvents === "string") {
    event = legacyEvents;
  } else {
    extra = legacyEvents.payload ?? {};
    event =
      platform === "native"
        ? legacyEvents.native
        : platform === "copy"
          ? legacyEvents.copy
          : legacyEvents.whatsapp;
  }
  if (!event) return;
  void track(event, {
    share_source: source,
    share_platform: platform,
    ...extra,
  });
}

async function openShare(
  kind: WaShareKind,
  url: string,
  message: string,
  opts: ShareOptions,
  nationCode?: string | null,
): Promise<ShareResult> {
  const platform = detectPlatform();
  const source = opts.source ?? `wa_${kind}`;
  const nationField =
    nationCode != null && nationCode !== "" ? { nation_code: nationCode } : {};
  const bridge = (channel: WaChannel) => {
    if (kind === "nation") emitNationLegacyBridge(channel, source, nationCode);
    emitCallerLegacyBridge(channel, source, opts.legacyEvents);
  };
  // Hashtag suffix — always include #Mundial2026 + #VerseSport on every
  // share, idempotent (skip if message already contains them).
  const hasMundial = /#Mundial\s*2026/i.test(message);
  const hasBrand = /#VerseSport/i.test(message);
  const tagParts = [
    hasMundial ? "" : "#Mundial2026",
    hasBrand ? "" : "#VerseSport",
  ].filter(Boolean);
  const hashtagTail = tagParts.length ? `\n${tagParts.join(" ")}` : "";
  const finalMessage = `${message}${hashtagTail}`;
  const text = `${finalMessage}\n${url}`;

  void track("wa_share_initiated", { kind, platform, source, ...nationField });

  // 1) Native Web Share API — best UX on Android / iOS.
  if (
    (opts.preferNative ?? true) &&
    typeof navigator !== "undefined" &&
    typeof (navigator as Navigator & { share?: (d: ShareData) => Promise<void> }).share === "function"
  ) {
    // 1a) Try with attachments first when caller supplied files AND the OS
    //     supports file shares. Falls through to plain link share on rejection.
    const files = opts.attachments && opts.attachments.length ? opts.attachments : null;
    const nav = navigator as Navigator & {
      canShare?: (d: { files?: File[] }) => boolean;
      share: (d: ShareData & { files?: File[] }) => Promise<void>;
    };
    if (files && typeof nav.canShare === "function" && nav.canShare({ files })) {
      try {
        await nav.share({ title: "VerseSport · Mundial FIFA 2026", text: finalMessage, url, files });
        void track("wa_share_completed", { kind, platform, channel: "native_share", source, ...nationField });
        void track("wa_share_destination", { kind, platform, channel: "native_share", source, ...nationField });
        bridge("native_share");
        return { ok: true, channel: "native_share", url, message: finalMessage };
      } catch (err: unknown) {
        if ((err as { name?: string })?.name === "AbortError") {
          void track("wa_share_failed", { kind, platform, channel: "native_share", reason: "aborted", ...nationField });
          return { ok: false, channel: "native_share", url, message: finalMessage };
        }
        // Fall through to plain link share below.
      }
    }
    try {
      await (navigator as Navigator & { share: (d: ShareData) => Promise<void> })
        .share({ title: "VerseSport · Mundial FIFA 2026", text: finalMessage, url });
      void track("wa_share_completed", { kind, platform, channel: "native_share", source, ...nationField });
      void track("wa_share_destination", { kind, platform, channel: "native_share", source, ...nationField });
      bridge("native_share");
      return { ok: true, channel: "native_share", url, message: finalMessage };
    } catch (err: unknown) {
      // User cancelled or share failed — fall through to wa.me.
      if ((err as { name?: string })?.name === "AbortError") {
        void track("wa_share_failed", { kind, platform, channel: "native_share", reason: "aborted", ...nationField });
        return { ok: false, channel: "native_share", url, message: finalMessage };
      }
    }
  }

  // 2) Mobile → try whatsapp:// scheme first, fall back to wa.me.
  if (isMobile(platform)) {
    try {
      const appUrl = whatsappAppLink(text);
      // Direct navigation works on iOS; Android allows window.open.
      if (typeof window !== "undefined") {
        const w = window.open(appUrl, "_blank", "noopener,noreferrer");
        // iOS in-app browsers return null — fall back to wa.me via location.
        if (!w) window.location.href = appUrl;
      }
      void track("wa_share_completed", { kind, platform, channel: "whatsapp_app", source, ...nationField });
      void track("wa_share_destination", { kind, platform, channel: "whatsapp_app", source, ...nationField });
      bridge("whatsapp_app");
      return { ok: true, channel: "whatsapp_app", url, message: finalMessage };
    } catch { /* fall through */ }
  }

  // 3) Desktop / WhatsApp Web fallback.
  try {
    const link = waMeLink(text);
    if (typeof window !== "undefined") window.open(link, "_blank", "noopener,noreferrer");
    void track("wa_share_completed", { kind, platform, channel: "wa_me", source, ...nationField });
    void track("wa_share_destination", { kind, platform, channel: "wa_me", source, ...nationField });
    bridge("wa_me");
    return { ok: true, channel: "wa_me", url, message: finalMessage };
  } catch (err: unknown) {
    void track("wa_share_failed", { kind, platform, channel: "wa_me", reason: String((err as Error)?.message ?? err), ...nationField });
    return { ok: false, channel: "wa_me", url, message: finalMessage };
  }
}

export async function sharePrediction(
  p: PredictionShareInput,
  opts: ShareOptions = {},
): Promise<ShareResult> {
  const url = opts.campaignOverride
    ? buildUrl("prediction", p.predictionId ? `/?pred=${encodeURIComponent(p.predictionId)}` : "/", opts.campaignOverride)
    : buildPredictionUrl(p);
  const enriched = await withPremiumAttachment(opts, () =>
    buildAttachment(payloadForPrediction(p), "versesport-prediccion.png"),
  );
  return openShare("prediction", url, buildPredictionMessage(p, opts.lang), enriched, p.nationCode);
}

export async function shareVictory(
  v: VictoryShareInput,
  opts: ShareOptions = {},
): Promise<ShareResult> {
  const path = opts.customPath ?? (v.predictionId ? `/?win=${encodeURIComponent(v.predictionId)}` : "/");
  const url = opts.customPath || opts.campaignOverride
    ? buildUrl("victory", path, opts.campaignOverride)
    : buildVictoryUrl(v);
  const enriched = await withPremiumAttachment(opts, () =>
    buildAttachment(payloadForVictory(v), "versesport-victoria.png"),
  );
  return openShare("victory", url, buildVictoryMessage(v, opts.lang), enriched, v.nationCode);
}

export async function shareChallenge(
  c: ChallengeShareInput,
  opts: ShareOptions = {},
): Promise<ShareResult> {
  const url = opts.campaignOverride
    ? buildUrl("challenge", `/challenge/${encodeURIComponent(c.code)}`, opts.campaignOverride)
    : buildChallengeUrl(c);
  const enriched = await withPremiumAttachment(opts, () =>
    buildAttachment(payloadForChallenge(c), "versesport-reto.png"),
  );
  return openShare("challenge", url, buildChallengeMessage(c, opts.lang), enriched, c.nationCode);
}

export async function shareNation(
  n: NationShareInput,
  opts: ShareOptions = {},
): Promise<ShareResult> {
  const url = opts.campaignOverride
    ? buildUrl("nation", buildNationShareLandingUrl(n.nationCode).replace(/^https?:\/\/[^/]+/, ""), opts.campaignOverride)
    : buildNationUrl(n);
  const enriched = await withPremiumAttachment(opts, () =>
    buildAttachment(payloadForNation(n), "versesport-nacion.png"),
  );
  return openShare("nation", url, buildNationMessage(n, opts.lang), enriched, n.nationCode);
}

/** Copy-to-clipboard fallback for surfaces that can't open new windows. */
export async function copyShare(
  kind: WaShareKind,
  url: string,
  message: string,
  source = `wa_${kind}_copy`,
  nationCode?: string | null,
): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.clipboard) return false;
  const nationField =
    nationCode != null && nationCode !== "" ? { nation_code: nationCode } : {};
  try {
    await navigator.clipboard.writeText(`${message}\n${url}`);
    void track("wa_share_completed", { kind, platform: detectPlatform(), channel: "copy", source, ...nationField });
    void track("wa_share_destination", { kind, platform: detectPlatform(), channel: "copy", source, ...nationField });
    if (kind === "nation") emitNationLegacyBridge("copy", source, nationCode);
    return true;
  } catch {
    void track("wa_share_failed", { kind, channel: "copy", reason: "clipboard_denied", ...nationField });
    return false;
  }
}